import { SortingState } from "@tanstack/react-table";
import { PlusInSquareIcon } from "assets/icons";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useDebouncedValue } from "hooks";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { useRefetchQuery } from "libs/reactQuery";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Pagination, SearchInput } from "ui";

import {
  DELIVERY_COMPANIES_QUERY_KEY,
  useGetDeliveryCompanies,
  useCreateDeliveryCompany,
  useUpdateDeliveryCompany,
  useDeleteDeliveryCompany,
  useToggleDeliveryCompanyActive
} from "../../apis";
import { CompaniesTable, CompanyFormModal } from "../../components";
import {
  DeliveryCompany,
  DeliveryCompanyFormValues,
  DeliveryCompanyListItem
} from "../../deliveryCompanies.types";

import styles from "./Companies.module.scss";

const Companies = () => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const debouncedSearch = useDebouncedValue(searchValue, 300);

  const sortParam = sorting[0];
  const requestParams = useMemo(
    () => ({
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      skip: (page - 1) * GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch || undefined,
      orderBy: sortParam?.id,
      order: sortParam ? (sortParam.desc ? "DESC" : "ASC") : undefined
    }),
    [page, debouncedSearch, sortParam]
  ) as Parameters<typeof useGetDeliveryCompanies>[0];

  const { companies, meta, isLoading } = useGetDeliveryCompanies(requestParams);
  const refetchCompanies = useRefetchQuery([DELIVERY_COMPANIES_QUERY_KEY]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editableCompany, setEditableCompany] =
    useState<DeliveryCompany | null>(null);

  const handleSuccess = async (message: string) => {
    setIsFormOpen(false);
    setEditableCompany(null);
    toast.success(message);
    await refetchCompanies();
  };

  const createMutation = useCreateDeliveryCompany({
    onSuccess: () => {
      void handleSuccess(t("Company created"));
    }
  });
  const updateMutation = useUpdateDeliveryCompany({
    onSuccess: () => {
      void handleSuccess(t("Company updated"));
    }
  });
  const deleteMutation = useDeleteDeliveryCompany({
    onSuccess: () => {
      toast.success(t("Company deleted"));
      void refetchCompanies();
    }
  });
  const toggleMutation = useToggleDeliveryCompanyActive({
    onSuccess: () => {
      void refetchCompanies();
    }
  });

  const openCreate = () => {
    setEditableCompany(null);
    setIsFormOpen(true);
  };

  const openEdit = (company: DeliveryCompany) => {
    setEditableCompany(company);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditableCompany(null);
  };

  const handleSubmit = (values: DeliveryCompanyFormValues) => {
    const capacity = typeof values.capacity === "number" ? values.capacity : 0;
    if (editableCompany) {
      updateMutation.mutate({
        id: editableCompany.id,
        name: values.name,
        email: values.email,
        password: values.password ? values.password : undefined,
        capacity,
        phoneNumber: values.phoneNumber || undefined,
        address: values.address || undefined,
        avatarMediaId: values.avatarMediaId
      });
    } else {
      createMutation.mutate({
        name: values.name,
        email: values.email,
        password: values.password,
        capacity,
        phoneNumber: values.phoneNumber || undefined,
        address: values.address || undefined,
        avatarMediaId: values.avatarMediaId ?? undefined
      });
    }
  };

  const handleDelete = (company: DeliveryCompanyListItem) => {
    if (window.confirm(t("Delete this company?"))) {
      deleteMutation.mutate(company.id);
    }
  };

  const handleToggleActive = (
    company: DeliveryCompanyListItem,
    isActive: boolean
  ) => {
    toggleMutation.mutate({ id: company.id, isActive });
  };

  const totalItems = meta?.itemCount ?? 0;
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <ContentLayout
      title={
        <span className={styles.companies__title}>
          {t("Companies")}
          <span className={styles.companies__count}>({totalItems})</span>
        </span>
      }
      action={
        <Button onClick={openCreate} icon={<PlusInSquareIcon />}>
          {t("New Company")}
        </Button>
      }
    >
      <BoxShadowLayout className={styles.companies__content}>
        <div className={styles.companies__header}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={t("Search companies...")}
          />
        </div>

        <CompaniesTable
          companies={companies ?? []}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          onEdit={openEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
        />

        <Pagination
          currentPage={page}
          onChange={setPage}
          perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
          totalItems={totalItems}
        />
      </BoxShadowLayout>

      <CompanyFormModal
        isOpen={isFormOpen}
        editableCompany={editableCompany}
        isSubmitting={isSubmitting}
        onClose={closeForm}
        onSubmit={handleSubmit}
      />
    </ContentLayout>
  );
};

export default Companies;
